import { Component, OnInit } from '@angular/core';
import { TableComponent } from '@shared/components/table/table.component';
import { meterpointQueryResultTable } from '@shared/table-configs/meteringpoint/meterpoint-query-result.table';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { AuthRoles, MeteringPointDto, MeterQueryResultDto, MeterService, } from '@app/api/dmc';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { HeaderService } from '@app/core/services/header.service';
import { ModalService } from '@app/core/services/modal.service';
import {
  CreateMeterModalComponent
} from '@shared/modals/meteringpoint/meter/create-meter-modal/create-meter-modal.component';
import {
  StopMeterModalComponent
} from '@shared/modals/meteringpoint/meter/stop-meter-modal/stop-meter-modal.component';
import { meteringPointMeterMenuAction } from '@views/meteringpoint/meteringpoint-meter/meteringpoint-meter.action-menu';
import {
  StopControlMeterModalComponent
} from '@shared/modals/meteringpoint/meter/stop-control-meter-modal/stop-control-meter-modal.component';
import {
  CreateControlMeterModalComponent
} from '@shared/modals/meteringpoint/meter/create-control-meter-modal/create-control-meter-modal.component';
import { HtxMeterModalComponent } from '@shared/modals/meter/htx-meter-modal/htx-meter-modal.component';
import { StateManagerService } from '@shared/state/state-manager.service';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { hasRole } from '@app/core/guards/has-role.guard';

@Component({
  selector: 'app-meteringpoint-meter',
  imports: [
    TableComponent
  ],
  templateUrl: './meteringpoint-meter.component.html',
  styleUrl: './meteringpoint-meter.component.css'
})
export class MeteringpointMeterComponent implements OnInit {
  /** ID **/
  public id?: string;

  /** Actions **/
  public mpWriter = hasRole([AuthRoles.MeteringPointWriter]);
  public actions: MenuAction[] = [
    {
      ...meteringPointMeterMenuAction[0],
      callback: () => this.openCreateMeterModal(),
      disabled: () => !this.mpWriter
    },
    {
      ...meteringPointMeterMenuAction[1],
      callback: () => this.openStopMeterModal(),
      disabled: () => !this.mpWriter || this.selectedElements.length == 0 && this.selectedElements[0]?.verificationMeter,
    },
    {
      ...meteringPointMeterMenuAction[2],
      callback: () => this.openCreateControlMeterModal(),
      disabled: () => !this.mpWriter
    },
    {
      ...meteringPointMeterMenuAction[3],
      callback: () => this.openStopControlMeterModal(),
      disabled: () => !this.mpWriter || this.selectedElements.length == 0 || !this.selectedElements[0]?.verificationMeter,
    },
    {
      ...meteringPointMeterMenuAction[4],
      callback: () => this.openHtxModal(),
      disabled: () => !this.mpWriter
    },
  ];

  /** Table **/
  public meterQueryResultTable: TableConfig<MeterQueryResultDto> = {
    ...meterpointQueryResultTable,
    contextActions: [
      this.actions[1],
      this.actions[3],
      this.actions[4],
    ],
    refreshable: true,
    refresh: () => this.fetchMeters(),
  };

  /** Table Data **/
  public meterQueryResultData: MeterQueryResultDto[] = [];

  /** MeteringPoint **/
  public meteringPoint?: MeteringPointDto;

  /** Selected Elements **/
  public selectedElements: any[] = [];


  constructor(
    private readonly route: ActivatedRoute,
    private readonly meterService: MeterService,
    private readonly headerMenuService: HeaderService,
    private readonly modalService: ModalService,
    private readonly stateManagerService: StateManagerService,
  ) {
    /* Set Header Actions */
    this.headerMenuService.setActions(this.actions);
  }

  public ngOnInit(): void {
    /** Have to get ID from parent **/
    this.route.parent?.paramMap.subscribe((q: ParamMap) => {
      this.id = q.get('id')!;

      this.stateManagerService.getMeteringPoint(this.id).subscribe((meteringPoint: MeteringPointDto) => {
        this.meteringPoint = meteringPoint;
      });


      this.fetchMeters();
    });
  }

  public fetchMeters(): void {
    this.meterService.meterQueryMeterPost({
      meteringPointIdentification: this.id
    }).subscribe((result: MeterQueryResultDto[]) => {
      this.meterQueryResultData = result;
    });
  }

  public openCreateMeterModal(): void {
    this.modalService.show({
      component: CreateMeterModalComponent,
      title: 'meteringpoint.meter.create.title',
      actions: [
        { type: 'primary', label: 'common.create', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        meteringPoint: this.meteringPoint
      }
    }).subscribe(() => {
      this.fetchMeters();
    })
  }

  public openStopControlMeterModal(): void {
    this.modalService.show({
      component: StopControlMeterModalComponent,
      title: 'Stop kontrolmåler',
      actions: [
        { type: 'primary', label: 'common.stop', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        element: this.selectedElements[0],
        meteringPointIdentification: this.meteringPoint?.identification
      }
    }).subscribe(() => {
      this.fetchMeters();
    })
  }

  public openStopMeterModal(): void {
    this.modalService.show({
      component: StopMeterModalComponent,
      title: 'meteringpoint.meter.stop.title',
      actions: [
        { type: 'primary', label: 'meteringpoint.meter.stop.action', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        meterQuery: this.selectedElements[0],
        id: this.id,
        meteringPoint: this.meteringPoint
      }
    }).subscribe(() => {
      this.fetchMeters();
    })
  }

  public openHtxModal(): void {
    this.modalService.show({
      component: HtxMeterModalComponent,
      title: 'meteringpoint.meter.htx.title',
      actions: [
        { type: 'primary', label: 'system.button.save', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        meteringPoint: this.meteringPoint,
        selectedMeter: this.selectedElements[0] || null
      }
    }).subscribe(() => {
      this.fetchMeters()
    })
  }

  public openCreateControlMeterModal(): void {
    this.modalService.show({
      component: CreateControlMeterModalComponent,
      title: 'meteringpoint.meter.createcontrolmeter.title',
      actions: [
        { type: 'primary', label: 'common.create', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {meteringPoint: this.meteringPoint}
    }).subscribe(() => {
      this.fetchMeters();
    })
  }

}
